import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SecondaryButton } from "@financial-edge/shared";
import AuthAlert from "../components/auth/AuthAlert";
import AuthShell from "../components/auth/AuthShell";
import SecureLaunchExperience from "../components/auth/SecureLaunchExperience";
import PrimaryButton from "../components/layout/PrimaryButton";
import { buildSupportReference } from "../utils/auth/authStatusCatalog";
import { buildLaunchSearch, formatIntentDestinationReference, getEffectiveLaunchContext, getIntentDetails, parseIntent, persistLaunchContext } from "../utils/auth/intent";
import {
    mapResolutionToStatusReason,
    normaliseResolution,
    resolveBackendAccess,
    resolveSourceScopedPortalDestinationUrl,
    RESOLUTION_STATUSES,
} from "../utils/auth/accessResolution";
import { logAuthFlowInfo, logAuthFlowWarning } from "../utils/auth/authFlowLogger";
import { BOOTSTRAP_REASON_CODES, buildBootstrapDestinationUrl, issuePortalBootstrap } from "../utils/auth/portalBootstrap";
import { getValidatedBrowserSession, signOutBrowserSession } from "../utils/session/browserSession";
import { SESSION_LIFECYCLE_STATES } from "../utils/session/sessionLifecycle";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

const AUTO_LAUNCH_GUARD_PREFIX = "login-screen:auto-launch:";
const APPLICATION_FORM_ROUTE_SEGMENT = "form";
const EXPLICIT_INTENT_AUTO_LAUNCH_PORTALS = new Set(["application", "broker", "lender", "investor", "client"]);
const SESSION_FAILURE_LOGIN_REDIRECTS = Object.freeze({
    missing: {
        reason: "missing_session",
        code: "AUTH_SESSION_REQUIRED",
    },
    invalid: {
        reason: "session_invalid",
        code: "AUTH_SESSION_INVALID",
    },
    expired: {
        reason: "session_expired",
        code: "AUTH_SESSION_EXPIRED",
    },
    revoked: {
        reason: "session_revoked",
        code: "AUTH_SESSION_REVOKED",
    },
    accessDenied: {
        reason: "access_denied",
        code: "AUTH_PORTAL_ACCESS_DENIED",
    },
    unavailable: {
        reason: "session_unable_to_validate",
        code: "AUTH_SESSION_VALIDATION_FAILED",
    },
});
const BROKER_ORIGIN_DIRECT_SOURCES = new Set([
    "broker-portal-proposal",
    "broker-portal-client-application",
]);

function getBrokerPortalBaseUrl() {
    return String(
        process.env.REACT_APP_BROKER_PORTAL_URL ||
        "http://localhost:3014/broker"
    ).trim().replace(/\/+$/g, "");
}

function buildDirectLaunchDestinationUrl(destinationUrl, returnTo = null, { brokerageId = null } = {}) {
    const baseUrl = new URL(destinationUrl, window.location.origin);
    const targetUrl = returnTo ? new URL(returnTo, baseUrl) : baseUrl;
    const resolvedBrokerageId = String(brokerageId || "").trim();
    const requiresBrokerageContext = targetUrl.pathname === "/application"
        || targetUrl.pathname === "/start"
        || targetUrl.pathname.startsWith("/apply/");

    if (resolvedBrokerageId && requiresBrokerageContext && !targetUrl.searchParams.has("_xctx") && !targetUrl.searchParams.has("brokerageId")) {
        targetUrl.searchParams.set("_xctx", resolvedBrokerageId);
    }

    return targetUrl.toString();
}

function parseResumeStep(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getLatestApplicationContext(resolution, session) {
    return (
        resolution?.accessContext?.sharedAccessContext?.application?.latestApplication ||
        session?.access?.sharedAccessContext?.application?.latestApplication ||
        null
    );
}

function getLatestApplicationBrokerageId(latestApplication) {
    return String(
        latestApplication?.brokerageId ||
        latestApplication?._xctx ||
        latestApplication?.companyId ||
        ""
    ).trim() || null;
}

function shouldScopeApplicationReturnTo(pathname) {
    return (
        pathname === "/" ||
        pathname === "/application" ||
        pathname === "/application/start" ||
        pathname === "/start" ||
        pathname === "/cals"
    );
}

function extractScopedApplicationId(pathname) {
    const match = String(pathname || "").match(/^\/apply\/([^/]+)(?:\/|$)/);

    if (!match?.[1]) {
        return "";
    }

    try {
        return decodeURIComponent(match[1]);
    } catch (_error) {
        return match[1];
    }
}

function buildApplicationLaunchReturnTo(returnTo, { resolution, session, brokerageId = null } = {}) {
    const baseUrl = typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const latestApplication = getLatestApplicationContext(resolution, session);
    const applicationId = String(
        latestApplication?.applicationId ||
        latestApplication?.appId ||
        latestApplication?.id ||
        ""
    ).trim();
    const resumeStep = parseResumeStep(
        latestApplication?.progress?.highestStepReached ??
        latestApplication?.highestStepReached ??
        latestApplication?.resumeStep
    );
    const targetUrl = new URL(returnTo || "/application", baseUrl);
    const scopedApplicationId = extractScopedApplicationId(targetUrl.pathname);
    const hasScopedApplicationReturnTo = Boolean(scopedApplicationId);
    const isApplicationPath =
        shouldScopeApplicationReturnTo(targetUrl.pathname) ||
        hasScopedApplicationReturnTo;

    if (
        applicationId &&
        (
            shouldScopeApplicationReturnTo(targetUrl.pathname) ||
            targetUrl.pathname === `/apply/${applicationId}` ||
            (hasScopedApplicationReturnTo && scopedApplicationId !== applicationId)
        )
    ) {
        targetUrl.pathname = `/apply/${encodeURIComponent(applicationId)}/${APPLICATION_FORM_ROUTE_SEGMENT}`;
    } else if (!applicationId && hasScopedApplicationReturnTo) {
        targetUrl.pathname = "/application";
    }

    if (resumeStep !== null && isApplicationPath && !targetUrl.searchParams.has("step")) {
        targetUrl.searchParams.set("step", String(resumeStep));
    }

    const resolvedBrokerageId = String(
        brokerageId || getLatestApplicationBrokerageId(latestApplication) || ""
    ).trim();
    if (resolvedBrokerageId && isApplicationPath && !targetUrl.searchParams.has("_xctx") && !targetUrl.searchParams.has("brokerageId")) {
        targetUrl.searchParams.set("_xctx", resolvedBrokerageId);
    }

    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
}

function buildAutoLaunchGuardKey({ requestId, requestedIntent, source, returnTo, portalId }) {
    return `${AUTO_LAUNCH_GUARD_PREFIX}${requestId || "no-request-id"}:${requestedIntent || "unknown"}:${source || "unknown"}:${returnTo || "/"}:${portalId || "unknown"}`;
}

function buildLoginRedirectPath(search, failure = SESSION_FAILURE_LOGIN_REDIRECTS.missing) {
    const params = new URLSearchParams(search || "");
    params.set("authReason", failure.reason || SESSION_FAILURE_LOGIN_REDIRECTS.invalid.reason);
    params.set("authCode", failure.code || SESSION_FAILURE_LOGIN_REDIRECTS.invalid.code);
    if (failure.requestId) {
        params.set("requestId", failure.requestId);
    }

    const query = params.toString();
    return query ? `/login?${query}` : "/login";
}

function getSessionValidationFailure(error) {
    if (!error) {
        return SESSION_FAILURE_LOGIN_REDIRECTS.missing;
    }

    const code = String(error?.code || "").trim();
    const status = Number(error?.status || 0);
    const requestId = String(error?.requestId || "").trim();
    let baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.invalid;

    if (code === "CORE_RUNTIME_UNAVAILABLE" || status === 503 || /network|failed to fetch|load failed/i.test(error?.message || "")) {
        baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.unavailable;
    } else if (/expired/i.test(code)) {
        baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.expired;
    } else if (/revoked/i.test(code)) {
        baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.revoked;
    } else if (status === 403 || /access.*denied|portal.*denied|not.*authori[sz]ed/i.test(code)) {
        baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.accessDenied;
    } else if (status === 401 || /invalid|unauthenticated|unauthori[sz]ed/i.test(code)) {
        baseFailure = SESSION_FAILURE_LOGIN_REDIRECTS.invalid;
    }

    return {
        ...baseFailure,
        code: code || baseFailure.code,
        requestId: requestId || null,
    };
}

function redirectToLogin(navigate, search, failure) {
    navigate(buildLoginRedirectPath(search, failure), { replace: true });
}

function buildLaunchSteps({ sessionChecked, resolution, requestedIntent, source, launching }) {
    const portalLabel = getIntentDetails(requestedIntent, source).label;

    return [
        {
            label: "Verify secure session",
            state: sessionChecked ? "complete" : "active",
        },
        {
            label: `Confirm ${portalLabel.toLowerCase()}`,
            state: resolution ? "complete" : sessionChecked ? "active" : "pending",
        },
        {
            label: "Open live dashboard",
            state: launching ? "active" : resolution?.status === RESOLUTION_STATUSES.confirmed ? "active" : "pending",
        },
    ];
}

function getAutoLaunchPortal(resolution, { requestedIntent, hasExplicitIntent }) {
    if (resolution?.status !== RESOLUTION_STATUSES.confirmed) {
        return null;
    }

    if (resolution.allowedPortals.length === 1) {
        const onlyPortal = resolution.allowedPortals[0];

        if (hasExplicitIntent && requestedIntent === "client" && onlyPortal.portalId !== requestedIntent) {
            return null;
        }

        return onlyPortal;
    }

    if (resolution.nextAction === "launch_portal" && resolution.preferredPortal) {
        return resolution.preferredPortal;
    }

    if (
        hasExplicitIntent &&
        EXPLICIT_INTENT_AUTO_LAUNCH_PORTALS.has(requestedIntent)
    ) {
        const requestedPortal = resolution.allowedPortals.find((portal) => portal.portalId === requestedIntent);

        if (requestedPortal) {
            return requestedPortal;
        }
    }

    return null;
}

export default function PortalSelection() {
    const navigate = useNavigate();
    const location = useLocation();
    const launchContext = getEffectiveLaunchContext(location.search);
    const returnTo = location.state?.returnTo || launchContext.returnTo;
    const requestedIntent = parseIntent(location.state?.intent || location.state?.resolution?.requestedIntent || launchContext.intent || "application");
    const preverifiedSession = location.state?.preverifiedSession || null;
    const [resolution, setResolution] = useState(() => {
        const stateResolution = location.state?.resolution || null;
        if (stateResolution) {
            return normaliseResolution(stateResolution, {
                intent: requestedIntent,
                source: launchContext.source,
                returnTo,
            });
        }
        return null;
    });
    const [currentSession, setCurrentSession] = useState(() => preverifiedSession);
    const [sessionChecked, setSessionChecked] = useState(() => Boolean(preverifiedSession));
    const [resolving, setResolving] = useState(false);
    const [portalActionError, setPortalActionError] = useState("");
    const [portalActionErrorCode, setPortalActionErrorCode] = useState("");
    const [launchingPortalId, setLaunchingPortalId] = useState(null);
    const autoLaunchAttemptedRef = useRef(new Set());
    const hasExplicitIntent = location.state?.hasExplicitIntent ?? launchContext.hasExplicitIntent;
    const rememberDevice = location.state?.rememberDevice === true || launchContext.rememberDevice === true;
    const verificationContext = location.state?.verificationContext || null;
    const intentDetails = getIntentDetails(requestedIntent, launchContext.source);
    const supportReference = buildSupportReference({
        reason: portalActionError ? portalActionErrorCode || BOOTSTRAP_REASON_CODES.issueFailed : resolution?.reasonCode,
        intent: requestedIntent,
        requestId: resolution?.requestId,
    });
    const isBrokerOriginDirectLaunch = BROKER_ORIGIN_DIRECT_SOURCES.has(String(launchContext.source || "").trim().toLowerCase()) && Boolean(returnTo);
    const autoLaunchPortal = getAutoLaunchPortal(resolution, {
        requestedIntent,
        hasExplicitIntent,
    });

    useEffect(() => {
        persistLaunchContext({
            intent: requestedIntent,
            source: launchContext.source,
            returnTo,
            brokerageId: launchContext.brokerageId,
            hasExplicitIntent,
            rememberDevice,
        });
    }, [hasExplicitIntent, launchContext.brokerageId, launchContext.source, rememberDevice, requestedIntent, returnTo]);

    useAuthPageMeta("Choose Workspace");

    const resolveLaunchSession = useCallback(async () => {
        if (currentSession) {
            return currentSession;
        }

        const validatedSession = await getValidatedBrowserSession();
        if (validatedSession) {
            setCurrentSession(validatedSession);
        }

        return validatedSession;
    }, [currentSession]);

    const performPortalLaunch = useCallback(async (portal, session, nextAction = resolution?.nextAction) => {
        setPortalActionError("");
        setPortalActionErrorCode("");
        setLaunchingPortalId(portal.portalId);

        logAuthFlowInfo("portal_redirect_selected", {
            portalId: portal.portalId,
            requestedIntent,
            nextAction,
        });

        if (!session) {
            redirectToLogin(navigate, location.search, SESSION_FAILURE_LOGIN_REDIRECTS.missing);
            return;
        }

        if (isBrokerOriginDirectLaunch) {
            const destinationUrl = new URL(returnTo, `${getBrokerPortalBaseUrl()}/`);
            window.location.assign(destinationUrl.toString());
            return;
        }

        const latestApplication = getLatestApplicationContext(resolution, session);
        const applicationBrokerageId = portal.portalId === "application"
            ? String(
                verificationContext?.brokerageId ||
                getLatestApplicationBrokerageId(latestApplication) ||
                ""
            ).trim() || null
            : null;
        const portalReturnTo = portal.portalId === "application"
            ? buildApplicationLaunchReturnTo(returnTo, {
                resolution,
                session,
                brokerageId: applicationBrokerageId,
            })
            : returnTo;
        const portalDestinationUrl = resolveSourceScopedPortalDestinationUrl(
            portal.portalId,
            portal.destinationUrl,
            launchContext.source
        );

        if (portal.portalId === "application") {
            logAuthFlowInfo("application_launch_confirmed", {
                requestedIntent,
                lifecycleState: SESSION_LIFECYCLE_STATES.accessResolved,
                brokerageId: applicationBrokerageId,
            });
        }

        const bootstrap = await issuePortalBootstrap({
            session,
            portal,
            requestedIntent,
            hasExplicitIntent,
            source: launchContext.source,
            returnTo: portalReturnTo,
            rememberDevice,
            verifiedEmail: verificationContext?.email || null,
            verifiedPhone: verificationContext?.phone || null,
        });

        const bootstrapDestinationUrl = resolveSourceScopedPortalDestinationUrl(
            portal.portalId,
            bootstrap.destinationUrl || portalDestinationUrl,
            launchContext.source
        );
        const resolvedBootstrapDestinationUrl = portal.portalId === "application"
            ? buildDirectLaunchDestinationUrl(bootstrapDestinationUrl, portalReturnTo, {
                brokerageId: applicationBrokerageId,
            })
            : bootstrapDestinationUrl;
        const destinationUrl = buildBootstrapDestinationUrl(
            resolvedBootstrapDestinationUrl,
            bootstrap.bootstrapCode,
            portalReturnTo,
            portal.portalId
        );

        window.location.assign(destinationUrl);
    }, [
        hasExplicitIntent,
        isBrokerOriginDirectLaunch,
        launchContext.source,
        location.search,
        navigate,
        requestedIntent,
        rememberDevice,
        resolution,
        returnTo,
        verificationContext?.email,
        verificationContext?.brokerageId,
        verificationContext?.phone,
    ]);

    useEffect(() => {
        let isMounted = true;

        async function validateSession() {
            if (preverifiedSession) {
                // OTP already validated this session. Bootstrap issuance validates it
                // again server-side, so a discarded /me request adds only contention.
                setSessionChecked(true);
                return;
            }

            try {
                const session = await getValidatedBrowserSession();
                if (!isMounted) {
                    return;
                }

                if (!session) {
                    redirectToLogin(navigate, location.search, SESSION_FAILURE_LOGIN_REDIRECTS.missing);
                    return;
                }

                setCurrentSession(session);
                setSessionChecked(true);
            } catch (error) {
                if (isMounted) {
                    redirectToLogin(navigate, location.search, getSessionValidationFailure(error));
                }
            }
        }

        validateSession();
        return () => {
            isMounted = false;
        };
    }, [location.search, navigate, preverifiedSession]);

    useEffect(() => {
        if (!sessionChecked || resolution) {
            return;
        }

        async function loadResolution() {
            setResolving(true);

            try {
                const session = await resolveLaunchSession();

                if (!session) {
                    redirectToLogin(navigate, location.search, SESSION_FAILURE_LOGIN_REDIRECTS.missing);
                    return;
                }

                const nextResolution = await resolveBackendAccess({
                    session,
                    intent: requestedIntent,
                    hasExplicitIntent,
                    source: launchContext.source,
                    returnTo,
                    verifiedPhone: verificationContext?.phone || null,
                    verifiedEmail: verificationContext?.email || null,
                    authUserId: session?.identity?.identityId || null,
                });

                setResolution(nextResolution);
            } catch (error) {
                logAuthFlowWarning("portal_resolution_failed", {
                    intent: requestedIntent,
                    source: launchContext.source,
                    message: error?.message || "unknown_error",
                });
                setResolution({
                    status: RESOLUTION_STATUSES.unresolved,
                    requestedIntent,
                    allowedPortals: [],
                    preferredPortal: null,
                    accessContext: null,
                    reasonCode: "no_confirmed_destination",
                    supportMessage: "We could not confirm a safe workspace for this session.",
                    nextAction: "retry",
                    requestId: error?.requestId || null,
                });
            } finally {
                setResolving(false);
            }
        }

        loadResolution();
    }, [hasExplicitIntent, launchContext.source, location.search, navigate, requestedIntent, resolution, returnTo, sessionChecked, resolveLaunchSession, verificationContext?.email, verificationContext?.phone]);

    useEffect(() => {
        if (!sessionChecked || !resolution || !autoLaunchPortal) {
            return;
        }

        if (launchingPortalId !== null || portalActionError) {
            return;
        }

        const guardKey = buildAutoLaunchGuardKey({
            requestId: resolution.requestId,
            requestedIntent,
            source: launchContext.source,
            returnTo,
            portalId: autoLaunchPortal.portalId,
        });

        if (autoLaunchAttemptedRef.current.has(guardKey)) {
            return;
        }

        let isActive = true;
        // Keep this cancellable for Strict Mode without delaying a verified login
        // just to play the launch animation.
        const timerId = window.setTimeout(async () => {
            if (!isActive || autoLaunchAttemptedRef.current.has(guardKey)) {
                return;
            }

            autoLaunchAttemptedRef.current.add(guardKey);

            try {
                const session = await resolveLaunchSession();

                if (!isActive || !session) {
                    autoLaunchAttemptedRef.current.delete(guardKey);
                    if (isActive) {
                        redirectToLogin(navigate, location.search, SESSION_FAILURE_LOGIN_REDIRECTS.missing);
                    }
                    return;
                }

                await performPortalLaunch(autoLaunchPortal, session, resolution.nextAction);
            } catch (error) {
                autoLaunchAttemptedRef.current.delete(guardKey);

                if (!isActive) {
                    return;
                }

                logAuthFlowWarning("portal_redirect_blocked", {
                    portalId: autoLaunchPortal.portalId,
                    requestedIntent,
                    message: error?.message || "unknown_error",
                    code: error?.code || null,
                    requestId: error?.requestId || null,
                });
                setPortalActionError("Your sign-in was confirmed, but we could not complete the secure handoff to this workspace. Try again or use the support reference below.");
                setPortalActionErrorCode(error?.code || "");
                setLaunchingPortalId(null);
            }
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timerId);
        };
    }, [
        autoLaunchPortal,
        launchContext.source,
        launchingPortalId,
        location.search,
        navigate,
        portalActionError,
        requestedIntent,
        resolution,
        returnTo,
        sessionChecked,
        performPortalLaunch,
        resolveLaunchSession,
    ]);

    const handlePortalSelect = async (portal) => {
        try {
            const session = await resolveLaunchSession();
            await performPortalLaunch(portal, session, resolution?.nextAction);
        } catch (error) {
            logAuthFlowWarning("portal_redirect_blocked", {
                portalId: portal.portalId,
                requestedIntent,
                message: error?.message || "unknown_error",
                code: error?.code || null,
                requestId: error?.requestId || null,
            });
            setPortalActionError("Your sign-in was confirmed, but we could not complete the secure handoff to this workspace. Try again or use the support reference below.");
            setPortalActionErrorCode(error?.code || "");
            setLaunchingPortalId(null);
        }
    };

    const handleLogout = async () => {
        await signOutBrowserSession();
        window.location.assign("/");
    };

    const canContinueDirectly = Boolean(autoLaunchPortal);
    const activePortal = resolution?.allowedPortals?.find((portal) => portal.portalId === launchingPortalId)
        || autoLaunchPortal
        || resolution?.preferredPortal
        || resolution?.allowedPortals?.[0]
        || {
            portalId: requestedIntent,
            label: intentDetails.label,
            description: intentDetails.description,
        };
    const shouldRenderLaunchExperience =
        !sessionChecked ||
        resolving ||
        !resolution ||
        ((canContinueDirectly || launchingPortalId !== null) && !portalActionError);

    if (shouldRenderLaunchExperience) {
        const title = !sessionChecked
            ? `Securing your ${intentDetails.shortLabel.toLowerCase()} sign-in`
            : !resolution || resolving
                ? `Preparing ${formatIntentDestinationReference(intentDetails.label)} access`
                : `Opening ${activePortal.label}`;
        const description = requestedIntent === "broker" && intentDetails.label !== "xPortal"
            ? "We are validating your Broker Portal access, preparing the live CRM session, and handing you straight into the dashboard."
            : `We are validating your access and preparing a secure handoff to ${formatIntentDestinationReference(activePortal.label)}.`;

        return (
            <SecureLaunchExperience
                eyebrow="Step 3 of 3"
                title={title}
                description={description}
                portalLabel={activePortal.label}
                note="Please keep this window open"
                launching={launchingPortalId !== null}
                steps={buildLaunchSteps({
                    sessionChecked,
                    resolution,
                    requestedIntent,
                    source: launchContext.source,
                    launching: launchingPortalId !== null || canContinueDirectly,
                })}
            />
        );
    }

    if (resolution.status !== RESOLUTION_STATUSES.confirmed || resolution.allowedPortals.length === 0) {
        const params = new URLSearchParams(buildLaunchSearch({ intent: requestedIntent, source: launchContext.source, returnTo }));
        const reason = mapResolutionToStatusReason(resolution);
        params.set("reason", reason);
        if (resolution?.reasonCode) {
            params.set("code", resolution.reasonCode);
        }
        if (resolution?.requestId) {
            params.set("requestId", resolution.requestId);
        }

        return (
            <Navigate
                to={`/auth-status?${params.toString()}`}
                replace
                state={{
                    intent: requestedIntent,
                    reason,
                    code: resolution?.reasonCode || null,
                    supportMessage: resolution?.supportMessage,
                    requestId: resolution?.requestId || null,
                    accessContext: resolution?.accessContext || null,
                    verificationContext: verificationContext || null,
                    launchSession: currentSession || preverifiedSession || null,
                    rememberDevice,
                }}
            />
        );
    }

    const primaryPortal = canContinueDirectly ? autoLaunchPortal : resolution.preferredPortal;

    return (
        <AuthShell
            eyebrow="Step 3 of 3"
            title={canContinueDirectly ? "Opening your workspace" : "Choose your approved workspace"}
            description={canContinueDirectly
                ? `Your access has been confirmed for ${primaryPortal.label}. We are preparing a secure handoff now.`
                : "Only workspaces confirmed by the access service are shown below."}
            align="center"
            footer={
                <div className="flex justify-center">
                    <SecondaryButton onClick={handleLogout} className="text-lg px-10 py-3">
                        Sign out
                    </SecondaryButton>
                </div>
            }
        >
            <div className="space-y-4">
                {resolution.supportMessage ? (
                    <AuthAlert tone="info" title="Session guidance">
                        {resolution.supportMessage}
                    </AuthAlert>
                ) : null}

                <div className="grid grid-cols-1 gap-4 w-full max-w-md mx-auto">
                    {resolution.allowedPortals.map((portal) => (
                        <button
                            key={portal.portalId}
                            type="button"
                            onClick={() => handlePortalSelect(portal)}
                            disabled={launchingPortalId !== null}
                            className="flex flex-col items-center justify-center px-6 py-5 border border-lightBorder rounded-2xl hover:border-primary hover:bg-surface transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60"
                        >
                            <div className="font-bold text-xl text-primary">{portal.label}</div>
                            <div className="mt-2 text-sm text-secondary">{portal.description}</div>
                            {launchingPortalId === portal.portalId ? (
                                <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-secondary">Preparing secure handoff</div>
                            ) : null}
                        </button>
                    ))}
                </div>

                {portalActionError ? (
                    <div className="space-y-3 max-w-md mx-auto">
                        <AuthAlert tone="error" title="Workspace handoff failed" live="assertive">
                            {portalActionError}
                        </AuthAlert>
                        <div className="text-xs text-secondary">Support reference: {supportReference}</div>
                        {portalActionErrorCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="font-mono">{portalActionErrorCode}</span>
                            </div>
                        ) : null}
                        <PrimaryButton
                            type="button"
                            onClick={() => {
                                const retryPortal = resolution.preferredPortal || resolution.allowedPortals[0];
                                if (retryPortal) {
                                    handlePortalSelect(retryPortal);
                                }
                            }}
                        >
                            Try secure handoff again
                        </PrimaryButton>
                    </div>
                ) : null}
            </div>
        </AuthShell>
    );
}
